import React from 'react';
import { AlertCircleIcon, Loader2Icon } from 'lucide-react';
import type { ConfirmationStatus } from '../../hooks/useMembershipConfirmation';
interface ConfirmationStatusPanelProps {
  status: Exclude<ConfirmationStatus, 'confirmed'>;
  onRetry: () => void;
}
interface PanelCopy {
  title: string;
  body: string;
  action: 'retry' | 'membership' | null;
}
const copy: Record<ConfirmationStatusPanelProps['status'], PanelCopy> = {
  verifying: {
    title: 'Confirming your membership…',
    body: 'This usually takes just a few seconds. Please keep this page open.',
    action: null
  },
  processing: {
    title: 'We’re still confirming your membership',
    body: 'Your details were received, but your membership hasn’t been confirmed yet. This can take a minute — check again shortly.',
    action: 'retry'
  },
  unavailable: {
    title: 'We can’t confirm your membership right now',
    body: 'Something interrupted the check. Your membership isn’t affected — please try again in a moment.',
    action: 'retry'
  },
  failed: {
    title: 'Your membership wasn’t completed',
    body: 'Checkout didn’t finish, so no membership was started. You can choose a membership and try again.',
    action: 'membership'
  },
  missing: {
    title: 'We couldn’t find your checkout details',
    body: 'Start from the membership page to choose a plan. If you’ve already completed checkout, contact us and we’ll help.',
    action: 'membership'
  }
};
const primaryButton = 'inline-flex items-center justify-center rounded-full bg-purple-700 px-8 py-3.5 text-sm font-semibold text-white transition-colors duration-200 ease-out hover:bg-purple-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600 focus-visible:ring-offset-2 focus-visible:ring-offset-ivory';

/** Non-confirmed states for /practitioners/welcome. */
export function ConfirmationStatusPanel({
  status,
  onRetry
}: ConfirmationStatusPanelProps) {
  const {
    title,
    body,
    action
  } = copy[status];
  const busy = status === 'verifying';
  return <section aria-live="polite" aria-busy={busy} className="max-w-xl mx-auto px-6 py-16 md:py-24 text-center" data-id="element-505" data-mp="3oc17towunx02">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-purple-100" data-id="element-506" data-mp="3efemkz0m33xi">
        {busy ? <Loader2Icon aria-hidden="true" className="h-6 w-6 animate-spin text-purple-700" data-id="element-507" data-mp="3jdznz1rlntnj" /> : <AlertCircleIcon aria-hidden="true" className="h-6 w-6 text-purple-700" data-id="element-508" data-mp="ayfzeg1vizet" />}
      </div>
      <h1 className="mt-7 font-serif text-3xl md:text-4xl text-charcoal leading-tight" data-id="element-509" data-mp="fy2ekv19ipq9">{title}</h1>
      <p className="mt-4 text-base text-charcoal/70 leading-relaxed" data-id="element-510" data-mp="19o7g7wtcf3w">{body}</p>

      {action && <div className="mt-9 flex flex-col items-center gap-4" data-id="element-511" data-mp="66xw3lcdomqo">
          {action === 'retry' ? <button type="button" onClick={onRetry} className={primaryButton} data-id="element-512" data-mp="twisp71gfiif">
              Check again
            </button> : <a href="/practitioners/membership" className={primaryButton} data-id="element-513" data-mp="yqdag4xi1nev">
              Go to membership
            </a>}
          <p className="text-sm text-charcoal/60" data-id="element-514" data-mp="2eg4eux8kmca6">
            Need help?{' '}
            <a href="mailto:hello@fementity.ca" className="font-medium text-purple-700 underline underline-offset-2 decoration-purple-300 hover:text-purple-900 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600" data-id="element-515" data-mp="2s6zr8jia3bii">
              hello@fementity.ca
            </a>
          </p>
        </div>}
    </section>;
}